import { useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { Text } from '@/components/Text';
import colors from '@/config/colors';
import { updateConsent } from '@/utils/consents';
import { styles } from './styles';
import { logError } from '@/utils/logHandling';
import {
  useResponsiveFontSize,
  useResponsiveWidth,
} from '@/hooks/useResponsiveDimensions';
import { useDialogBridge } from '@/hooks/useDialogBridge';

const digaClientErrorCodeConfig = {
  'error.digaCodes.expired': {
    header: 'Rezeptcode abgelaufen',
    body: 'Ungültiger Rezeptcode! Der eingegebene Rezeptcode ist abgelaufen. Bitte wenden Sie sich an Ihre Krankenkasse, um einen gültigen Code zu erhalten.',
  },
  'error.digaCodes.cancelled': {
    header: 'Rezeptcode storniert',
    body: 'Ungültiger Rezeptcode! Der eingegebene Rezeptcode wurde storniert. Bitte wenden Sie sich an Ihre Krankenkasse, um einen gültigen Code zu erhalten.',
  },
  'error.digaCodes.notFound': {
    header: 'Rezeptcode nicht gefunden',
    body: 'Ungültiger Rezeptcode! Der eingegebene Rezeptcode wurde nicht gefunden. Bitte prüfen Sie die Eingabe oder wenden Sie sich an Ihre Krankenkasse.',
  },
  'error.digaCodes.invalidFormat': {
    header: 'Anfrage oder Datei ungültig',
    body: [
      'Technischer Fehler! Bitte versuchen Sie es erneut. Sollte der Fehler weiterhin bestehen, wenden Sie sich bitte an unseren Kundenservice unter 089 24886 4440 oder per E-Mail an ',
      <Text>
        <Text style={styles.emailPopUp} key="email">
          info@neolexon.de
        </Text>
        .
      </Text>,
    ],
  },
  'error.digaCodes.serverError': {
    header: 'Serverfehler',
    body: 'Technischer Fehler! Der Rezeptcode kann momentan nicht verarbeitet werden. Bitte versuchen Sie es später erneut.',
  },
  fallback: {
    header: 'Ungültiger Rezeptcode',
    body: [
      'Bitte überprüfen Sie den Rezeptcode und versuchen Sie es erneut. ' +
        'Probleme beim Einlösen? Bitte melden Sie sich unter +49 (0)89 24886 4440 oder per E-mail an ',
      <Text>
        <Text style={styles.emailPopUp} key="email">
          info@neolexon.de
        </Text>
        .
      </Text>,
    ],
  },
};

export type DigaClientErrorCodeType = keyof typeof digaClientErrorCodeConfig;

interface DigaCodeModalProps {
  isModalVisible: boolean;
  setModalVisible: (visible: boolean) => void;
  isDeactivationPostponeConsentModalVisible: boolean;
  setDeactivationPostponeConsentModalVisible: (visible: boolean) => void;
  isModalErrorVisible: boolean;
  setModalErrorVisible: () => void;
  isModalErrorAPIVisible: boolean;
  setModalErrorAPIVisible: (visible: boolean) => void;
  isModalInternetErrorVisible: boolean;
  setModalInternetErrorVisible: (visible: boolean) => void;
  consentId: string | null;
  isSecondDigaCode: boolean;
  modalErrorAPIMessageKey: DigaClientErrorCodeType | null;
}

export function DigaMessages({
  isModalVisible,
  setModalVisible,

  isDeactivationPostponeConsentModalVisible,
  setDeactivationPostponeConsentModalVisible,

  isModalErrorVisible,
  setModalErrorVisible,

  isModalErrorAPIVisible,
  setModalErrorAPIVisible,

  isModalInternetErrorVisible,
  setModalInternetErrorVisible,

  consentId,

  isSecondDigaCode,

  modalErrorAPIMessageKey,
}: DigaCodeModalProps) {
  const internetErrorText =
    'Keine Verbindung zum neolexon Server. ' +
    'Bitte überprüfen Sie Ihre Internetverbindung und versuchen Sie es erneut. ' +
    '\n' +
    'Wenn das Problem besteht, melden Sie sich unter +49 (0)89 24886 4440 oder per E-mail an ';

  const email = 'info@neolexon.de';

  const deactivationPostponeConsentErrorText =
    'Die Einwilligung konnte nicht aktualisiert werden. Bitte versuchen Sie es später erneut. Sie finden die Einwillung zur späteren Account-Deaktivierung auch in Ihrem Nutzeraccount.';
  const [deactivationPostponeError, setDeactivationPostponeError] =
    useState('');

  const responsiveFontSize = useResponsiveFontSize(1);
  const getResponsiveFontSize = (factor: number) => responsiveFontSize * factor;

  const responsiveStyles = StyleSheet.create({
    successText: {
      fontSize: getResponsiveFontSize(1.9),
      color: colors.green,
      marginVertical: getResponsiveFontSize(0.5),
    },
    fontSize: {
      fontSize: getResponsiveFontSize(1.7),
    },
    instructions: {
      fontSize: getResponsiveFontSize(1.7),
      margin: getResponsiveFontSize(1.5),
    },
    errorText: {
      fontSize: getResponsiveFontSize(1.7),
      color: colors.red,
      marginVertical: getResponsiveFontSize(0.5),
    },
    container: { padding: useResponsiveWidth(2.4) },
  });

  async function updateDeactivationPostponeConsent(isConsentGiven: boolean) {
    try {
      if (consentId) {
        await updateConsent(consentId, isConsentGiven);
        return true;
      }
      return false;
    } catch (_error) {
      logError('Error updating deactivation postpone consent', _error);
      return false;
    }
  }

  useDialogBridge(
    isModalVisible,
    () => ({
      title: isSecondDigaCode
        ? 'Rezeptcode vorgeplant'
        : 'Rezeptcode eingelöst',
      content: isSecondDigaCode
        ? 'Ihr Rezeptcode wurde erfolgreich vorgeplant.'
        : 'Ihr Rezeptcode wurde erfolgreich eingelöst.',
      confirmAction: {
        label: 'OK',
        onPress: () => setModalVisible(false),
      },
    }),
    [isSecondDigaCode],
  );

  useDialogBridge(
    isDeactivationPostponeConsentModalVisible,
    () => ({
      title: 'Einwilligung zur Verlängerung der Account-Deaktivierung',
      contentComponent: () => (
        <View style={responsiveStyles.container}>
          <Text style={responsiveStyles.successText}>
            Ihr Rezeptcode wurde erfolgreich eingelöst.
          </Text>
          <Text style={responsiveStyles.fontSize}>
            Ich willige ein, dass mein Account mit meinen Daten nach Ablauf der
            90-tägigen Nutzungsdauer des Rezeptcodes für weitere 30 Tage
            bestehen bleibt. Auf diese Weise habe ich 30 Tage länger Zeit, bei
            Bedarf einen weiteren Rezeptcode direkt einzulösen und meinen
            Account weiterzunutzen. Sollte nach der Verlängerung von 30 Tagen
            kein Rezeptcode eingelöst worden sein, wird mein Account aufgrund
            von gesetzlichen Bestimmungen deaktiviert. Nach der Deaktivierung
            kann ich jederzeit eine Reaktivierung beantragen. Ich kann meine
            Einwilligung jederzeit widerrufen. Die Einwilligung ist optional.
          </Text>
          {!!deactivationPostponeError && (
            <Text style={responsiveStyles.errorText}>
              {deactivationPostponeError}
            </Text>
          )}
        </View>
      ),
      confirmAction: {
        label: 'Ich willige ein',
        onPress: async () => {
          if (await updateDeactivationPostponeConsent(true)) {
            setDeactivationPostponeConsentModalVisible(false);
          } else {
            setDeactivationPostponeError(deactivationPostponeConsentErrorText);
          }
        },
      },
      cancelAction: {
        label: 'Account unmittelbar nach Ablauf des Codes deaktivieren',
        onPress: async () => {
          await updateDeactivationPostponeConsent(false);
          setDeactivationPostponeConsentModalVisible(false);
        },
      },
      showCancelButton: true,
    }),
    [deactivationPostponeError, consentId],
  );

  useDialogBridge(
    isModalErrorVisible,
    () => ({
      title: 'Ungültiger Rezeptcode',
      contentComponent: () => (
        <Text style={responsiveStyles.instructions}>
          {digaClientErrorCodeConfig['fallback']?.body}
        </Text>
      ),
      confirmAction: {
        label: 'OK',
        onPress: () => setModalErrorVisible(),
      },
    }),
    [],
  );

  useDialogBridge(isModalErrorAPIVisible, () => {
    const config =
      modalErrorAPIMessageKey &&
      modalErrorAPIMessageKey in digaClientErrorCodeConfig
        ? digaClientErrorCodeConfig[modalErrorAPIMessageKey]
        : digaClientErrorCodeConfig['fallback'];
    return {
      title: config?.header,
      contentComponent: () => (
        <Text style={responsiveStyles.instructions}>{config?.body}</Text>
      ),
      confirmAction: {
        label: 'OK',
        onPress: () => setModalErrorAPIVisible(false),
      },
    };
  }, [modalErrorAPIMessageKey]);

  useDialogBridge(
    isModalInternetErrorVisible,
    () => ({
      title: 'Fehler bei Einlösen des Rezeptcodes',
      contentComponent: () => (
        <Text style={responsiveStyles.instructions}>
          {internetErrorText}
          <Text style={styles.emailPopUp}>{email}</Text>
        </Text>
      ),
      confirmAction: {
        label: 'OK',
        onPress: () => setModalInternetErrorVisible(false),
      },
    }),
    [],
  );

  return null;
}
